<template>
  <div
    :class="[
      'vue-numeric-input',
      size,
      controlsType === 'updown' ? 'updown' : ''
    ]"
    :style="widthStyle"
  >
    <input
      :name="name"
      ref="input"
      type="text"
      step="any"
      :class="inputClasses"
      :value="computedValue"
      :placeholder="placeholder"
      :max="max"
      :min="min"
      :style="inputStyle"
      @input="onInput"
      @change="onChange"
      @blur="onBlur"
      @focus="onFocus"
      :autofocus="autofocus"
      :disabled="disabled"
      :readonly="readonly || !isInput"
      :tabindex="tabindex"
      v-bind="$attrs"
      v-on="
        mousewheel
          ? { wheel: throttle(mouseWheelHandler, 6000) }
          : {
              wheel: function(evt) {
                evt.preventDefault();
              }
            }
      "
    />
    <button
      type="button"
      v-if="controls"
      class="input-btn btn-decrement"
      @mousedown.left="start(decrement)"
      @touchstart.prevent="start(decrement)"
      @touchend.prevent="stop"
      :disabled="disabled || minDisable"
      tabindex="-1"
      :style="buttonStyle"
    >
      <span class="btn-icon"></span>
    </button>
    <button
      type="button"
      v-if="controls"
      class="input-btn btn-increment"
      @mousedown.left="start(increment)"
      @touchstart.prevent="start(increment)"
      @touchend.prevent="stop"
      :disabled="disabled || maxDisable"
      tabindex="-1"
      :style="buttonStyle"
    >
      <span class="btn-icon"></span>
    </button>
  </div>
</template>
<script>
import { isNumber } from 'lodash'

const timeInterval = 100;
export default {
  name: "vue-numeric-input",
  props: {
    name: String,
    value: [String, Number],
    placeholder: String,
    min: {
      type: Number,
      default: Number.MIN_SAFE_INTEGER
    },
    max: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER
    },
    step: {
      type: Number,
      default: 1
    },
    align: {
      type: String,
      default: "left"
    },
    width: {
      type: String
    },
    size: {
      type: String,
      default: "normal",
      validator: value => {
        return ["small", "normal", "large"].includes(value);
      }
    },
    precision: {
      type: Number,
      validator(val) {
        return val >= 0 && Number.isInteger(val);
      }
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    controls: {
      type: Boolean,
      default: true
    },
    controlsType: {
      type: String,
      default: "plusminus"
    },
    controlsOffset: {
      type: Number,
      default: 0
    },
    mousewheel: {
      type: Boolean,
      default: false
    },
    isInput: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: null
    },
    tabindex: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      newValue: '',
      minDisable: false,
      maxDisable: false,
      interval: null,
      startTime: null,
      handler: Function
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if(val !== '') {
          if (this.precision) {
            val = this.toPrecision(val, this.precision);
          }
          if (isNumber(this.max) && val >= this.max) {
            this.newValue = this.max;
          } else if (isNumber(this.min) && val <= this.min) {
            this.newValue = this.min;
          } else {
            this.newValue = val;
          }

          if (this.newValue !== val) {
            this.$emit("input", this.newValue);
          }
        } else {
          this.newValue = ''
        }
      }
    }
  },
  methods: {
    /**
     * Function convert value to number
     * @param val
     * @returns {number | Number}
     */
    toNumber(val) {
      const n = /,/.test(val) ? +(val.replace(',','.')) : +val
      return isNaN(n) ? val : n;
    },
    /**
     * Function to return fixed decimal precision of input val
     * @param val
     * @param prec
     * @returns {number | Number}
     */
    toPrecision(val, precision) {
      if (precision === undefined) precision = this.numPrecision;
      return parseFloat(
        Math.round(val * Math.pow(10, precision)) / Math.pow(10, precision)
      );
    },
    /**
     * Function to get the precision of a v
     * @param value
     * @returns {number | Number}
     */
    getPrecision(value) {
      if (value === undefined) return 0;
      const valueString = value.toString();
      const dotPosition = valueString.indexOf(".");
      let precision = 0;
      if (dotPosition !== -1) {
        precision = valueString.length - dotPosition - 1;
      }
      return precision;
    },
    /**
     * Increment the current numeric value
     */
    increment() {
      if (!this.readonly && !this.disabled) {
        const val = this.computedValue || 0;
        const precisionFactor = Math.pow(10, this.numValuePrecision);
        const newVal =
          Math.round(precisionFactor * val + precisionFactor * this.step) /
          precisionFactor;
        if (newVal <= this.max) {
          this.minDisable = false;
          this.computedValue = newVal;
        } else {
          this.maxDisable = true;
        }
      }
    },
    /**
     * Decrement the current numeric value
     */
    decrement() {
      if (!this.readonly && !this.disabled) {
        const val = this.computedValue || 0;
        const precisionFactor = Math.pow(10, this.numValuePrecision);
        const newVal =
          Math.round(precisionFactor * val - precisionFactor * this.step) /
          precisionFactor;
        if (newVal >= this.min) {
          this.maxDisable = false;
          this.computedValue = newVal;
        } else {
          this.minDisable = true;
        }
      }
    },
    /**
     * Handle value on Input
     */
    onInput(event) {
      this.$nextTick(() => {
        if(/[0-9.,]/.test(event.data)) {
          if(/[.,]/.test(event.target.value.slice(0,-1)) && /[.,]/.test(event.data)) event.target.value = event.target.value.slice(0,-1)
          if(!/[0-9]/.test(event.target.value) && /[.,]/.test(event.data)) event.target.value = ''

          if(event.target.value && !['.', ','].includes(event.target.value.slice(-1))) this.computedValue = this.toNumber(event.target.value)
        } else {
          event.target.value = event.target.value.slice(0,-1)
        }
      });
    },
    /**
     *  Start a repetitive call to increment and decrement method after a timeInterval on mousedown event and will stop on mouseup event on controls
     * @param handler - increment or decrement method
     */
    start(handler) {
      document.addEventListener("mouseup", this.stop);
      this.startTime = new Date();
      this.handler = handler;
      clearInterval(this.interval);
      this.interval = setInterval(handler, timeInterval);
    },
    /**
     * clear interval on mouseup event and remove the listener
     * @param evt - event to be removed
     */
    stop(evt) {
      document.removeEventListener(evt.type, this.stop);
      if (new Date() - this.startTime < timeInterval) {
        this.handler();
      }
      clearInterval(this.interval);
      this.interval = null;
      this.handler = null;
      this.startTime = null;
      if (this.value !== this.computedValue)
        this.$emit("change", this.computedValue);
    },
    /**
     * On blur event trigger
     * @param event - blur event on input
     */
    onBlur(event) {
      if(['.', ','].includes(event.target.value.slice(-1))) {
        event.target.value = event.target.value.slice(0, -1)
      }

      this.$emit("blur", event);
    },
    /**
     * On focus event trigger on input
     * @param event
     */
    onFocus(event) {
      this.$emit("focus", event);
    },
    /**
     * On change event trigger on input
     * @param event
     */
    onChange() {
      this.$emit("change", this.computedValue);
    },
    /**
     * focus method to set the focus on input
     */
    focus() {
      if (!this.disabled) {
        this.$refs.input.focus();
      }
    },
    /**
     * blur to be trigger on input
     */
    blur() {
      this.$refs.input.blur();
    },
    mouseWheelHandler(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.deltaY < 0) {
        this.increment();
      } else {
        this.decrement();
      }
      return false;
    },
    throttle(fn, delay) {
      let lastCall = 0;
      return function(...args) {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
          return;
        }
        lastCall = now;
        return fn(...args);
      };
    }
  },
  computed: {
    computedValue: {
      get() {
        return this.newValue;
      },
      set(value) {
        this.newValue = value;
        this.$emit("input", value);
      }
    },
    numValuePrecision() {
      const stepPrecision = this.getPrecision(this.step);
      if (this.precision !== undefined) {
        return this.precision;
      } else {
        return Math.max(
          this.getPrecision(this.computedValue || 0),
          stepPrecision
        );
      }
    },
    inputClasses() {
      return [
        this.controls ? "" : "no-control",
        this.className ? this.className : "",
        "numeric-input"
      ];
    },
    inputStyle() {
      return { textAlign: this.align };
    },
    widthStyle() {
      let sizeWidth = "150px";
      if (this.size === "small") {
        sizeWidth = "100px";
      } else if (this.size === "large") {
        sizeWidth = "240px";
      }
      return { width: this.width ? `${this.width}` : sizeWidth };
    },
    buttonStyle() {
      return {
        right: `${this.controlsOffset}px`
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.interval);
    this.interval = null;
    this.handler = null;
    this.startTime = null;
  }
};
</script>
<style lang="scss">
$border-color_1: var(--theme-color-1);
$border-color_2: transparent transparent #111;
$border-color_3: #111 transparent transparent;

.vue-numeric-input {
  position: relative;
  box-sizing: border-box;
  .numeric-input {
    height: inherit;
    padding: 2px 2rem;
    box-sizing: border-box;
    font-size: inherit;
    border: 1px solid #cccccc;
    border-radius: 2px;
    display: block;
    line-height: 1.8rem;
    transition: all 0.1s ease 0s;
    width: 100%;
    -moz-appearance: textfield !important;
    &:focus {
      outline: none;
      border-color: $border-color_1;
    }
  }
  button {
    position: absolute;
    width: 1.8rem;
    padding: 0.2rem;
    margin: 0;
    cursor: pointer;
    text-align: center;
    transition: all 0.1s ease 0s;
    background: rgba(0, 0, 0, 0.1);
    border: solid rgba(0, 0, 0, 0.1);
    box-shadow: rgba(0, 0, 0, 0.1) -1px -1px 3px inset, rgba(255, 255, 255, 0.7) 1px 1px 3px inset;
    &:focus {
      outline: none !important;
    }
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
    &:active {
      box-shadow: rgba(0, 0, 0, 0.2) 0 1px 3px inset, rgba(255, 255, 255, 0.5) -1px -1px 4px inset;
    }
    &:disabled {
      opacity: 0.5;
      box-shadow: none;
      cursor: not-allowed;
    }
  }
  .numeric-input.no-control {
    padding: 2px 5px;
  }
  .btn-increment {
    right: 0;
    top: 0;
    bottom: 0;
    border-radius: 2px;
    border-width: 1px;
    .btn-icon {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      &:before {
        display: inline-block;
        visibility: visible;
        content: "";
        background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' fill='CurrentColor' viewBox='0 0 16 16'><path d='M8 0a1 1 0 0 1 1 1v6h6a1 1 0 1 1 0 2H9v6a1 1 0 1 1-2 0V9H1a1 1 0 0 1 0-2h6V1a1 1 0 0 1 1-1z'/></svg>");
        background-repeat: no-repeat;
        background-size: 65% 65%;
        background-position: center;
        width: 100%;
        height: 100%;
      }
      &:after {
        position: absolute;
        visibility: hidden;
        content: "";
      }
    }
  }
  .btn-decrement {
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 2px;
    border-width: 1px;
    .btn-icon {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      &:before {
        display: inline-block;
        visibility: visible;
        content: "";
        background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 16 16'><path d='M0 8a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1z'/></svg>");
        background-repeat: no-repeat;
        background-size: 65% 65%;
        background-position: center;
        width: 100%;
        height: 100%;
      }
      &:after {
        visibility: hidden;
        content: "";
        clear: both;
        height: 0;
      }
    }
  }
}
.vue-numeric-input.small {
  height: 20px;
  font-size: 12px;
  .numeric-input {
    padding: 2px 1.4rem;
  }
  button {
    width: 20px;
  }
}
.vue-numeric-input.large {
  height: 42px;
  font-size: 34px;
  .numeric-input {
    padding: 2px 2.8rem;
  }
  button {
    width: 40px;
  }
}
.numeric-input {
  &::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }
}
.vue-numeric-input.updown {
  .numeric-input {
    padding: 5px 2rem 5px 5px;
  }
  .input-btn {
    left: unset !important;
    bottom: unset !important;
    right: 0;
    top: 0;
    height: 50%;
    background: none;
    border: none;
    box-shadow: none;
    &:not(:last-child) {
      &:after {
        content: "";
        position: absolute;
        width: 60%;
        height: 1px;
        top: 0;
        right: 20%;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
      }
    }
    .btn-icon {
      &:before {
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 15L12 9L6 15' stroke='%237D7D7D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
        background-size: 100% 100%;
      }
      &:after {
        clear: both;
        height: 0;
      }
    }
    &.btn-decrement {
      top: unset;
      bottom: 0 !important;
      .btn-icon {
        &:before {
          background-image: url("data:image/svg+xml,%3Csvg width='25' height='24' viewBox='0 0 25 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.875 9L12.875 15L18.875 9' stroke='%237D7D7D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
          transform: unset;
        }
      }
    }
  }
}
</style>
